import { useEffect, useState } from 'react';
import { CreditCard, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import {
  browserHttpContext,
  digitsOnly,
  formatCardNumber,
  fallbackDeviceSession,
  loadOpenPay,
  tokenizeCard,
  type OpenPayStatic,
} from '@/lib/openpay';

interface OpenpayCheckoutProps {
  payload: Record<string, unknown>;
  formValid: boolean;
  onSuccess: (result: { folio: string; id_venta: number }) => void;
  onError: (message: string) => void;
}

const YEARS = Array.from({ length: 12 }, (_, i) => {
  const y = (new Date().getFullYear() + i) % 100;
  return String(y).padStart(2, '0');
});

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

export function OpenpayCheckoutSection({
  payload,
  formValid,
  onSuccess,
  onError,
}: OpenpayCheckoutProps) {
  const [config, setConfig] = useState<{
    enabled: boolean;
    merchantId: string;
    publicKey: string;
    sandbox: boolean;
  } | null>(null);
  const [openpay, setOpenpay] = useState<OpenPayStatic | null>(null);
  const [deviceSessionId, setDeviceSessionId] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [holder, setHolder] = useState('');
  const [number, setNumber] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cargaError, setCargaError] = useState<string | null>(null);

  useEffect(() => {
    api
      .openpayConfig()
      .then(setConfig)
      .catch(() =>
        setConfig({ enabled: false, merchantId: '', publicKey: '', sandbox: true }),
      );
  }, []);

  useEffect(() => {
    if (!config?.enabled || !config.merchantId || !config.publicKey) return;

    setCargaError(null);

    const ready = window.OpenPay;
    if (ready) {
      ready.setId(config.merchantId);
      ready.setApiKey(config.publicKey);
      ready.setSandboxMode(!!config.sandbox);
      setOpenpay(ready);
      return;
    }

    loadOpenPay(config.merchantId, config.publicKey, config.sandbox)
      .then(setOpenpay)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'No se pudo cargar OpenPay.js';
        setCargaError(msg);
      });
  }, [config?.enabled, config?.merchantId, config?.publicKey, config?.sandbox]);

  const handlePay = async () => {
    if (!formValid) {
      onError('Complete todos los campos obligatorios antes de pagar.');
      return;
    }
    if (!config?.merchantId || !config.publicKey) {
      onError('OpenPay no está configurado.');
      return;
    }

    const cardNumber = digitsOnly(number);
    const cvv2 = digitsOnly(cvv);
    const holderName = holder.trim();

    if (holderName.length < 3) {
      onError('Indique el nombre del tarjetahabiente.');
      return;
    }

    setProcesando(true);
    onError('');

    try {
      let instance = openpay ?? window.OpenPay ?? null;
      if (!instance) {
        instance = await loadOpenPay(config.merchantId, config.publicKey, config.sandbox);
      } else {
        instance.setId(config.merchantId);
        instance.setApiKey(config.publicKey);
        instance.setSandboxMode(!!config.sandbox);
      }
      setOpenpay(instance);

      if (!instance.card.validateCardNumber(cardNumber)) {
        throw new Error('El número de tarjeta no es válido.');
      }
      if (!instance.card.validateExpiry(month, year)) {
        throw new Error('La fecha de vencimiento no es válida.');
      }
      if (!instance.card.validateCVC(cvv2, cardNumber)) {
        throw new Error('El código CVV no es válido.');
      }

      const sessionId = deviceSessionId || fallbackDeviceSession();
      if (!deviceSessionId) setDeviceSessionId(sessionId);
      const tokenId = await tokenizeCard(instance, {
        card_number: cardNumber,
        holder_name: holderName,
        expiration_year: year,
        expiration_month: month,
        cvv2,
      });

      const result = await api.openpayCharge({
        ...payload,
        token_id: tokenId,
        device_session_id: sessionId,
        http_context: browserHttpContext(),
        redirect_url: `${window.location.origin}/checkout`,
      });

      if (result.status === 'charge_pending' && result.redirect_url) {
        window.location.href = result.redirect_url;
        return;
      }

      if (result.status === 'completed' && result.folio) {
        onSuccess({ folio: result.folio, id_venta: result.id_venta ?? 0 });
        return;
      }

      onError('El pago no se completó. Intente de nuevo.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Error al procesar el pago');
    } finally {
      setProcesando(false);
    }
  };

  if (!config) {
    return <p className="text-sm text-surface-800/50">Verificando OpenPay...</p>;
  }

  if (!config.enabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>OpenPay aún no configurado.</strong>
        <p className="mt-1">
          Copie <code className="text-xs">openpay.local.php.example</code> como{' '}
          <code className="text-xs">openpay.local.php</code> y pegue Merchant ID, llave
          pública y privada del{' '}
          <a
            href="https://sandbox-dashboard.openpay.mx/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            dashboard de OpenPay
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="flex items-center gap-1.5 text-xs text-surface-500">
        <Lock className="h-3.5 w-3.5" />
        Pago seguro con OpenPay · los datos de la tarjeta no pasan por Alarcom
      </p>
      <input
        className="input-field"
        placeholder="Nombre en la tarjeta *"
        autoComplete="cc-name"
        value={holder}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onChange={(e) => setHolder(e.target.value)}
      />
      <input
        className="input-field"
        placeholder="Número de tarjeta *"
        inputMode="numeric"
        autoComplete="cc-number"
        value={number}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onChange={(e) => setNumber(formatCardNumber(e.target.value))}
      />
      <div className="grid grid-cols-3 gap-3">
        <select
          className="input-field"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Mes de vencimiento"
        >
          <option value="">Mes</option>
          {MONTHS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="input-field"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          aria-label="Año de vencimiento"
        >
          <option value="">Año</option>
          {YEARS.map((y) => (
            <option key={y} value={y}>
              20{y}
            </option>
          ))}
        </select>
        <input
          className="input-field"
          placeholder="CVV *"
          inputMode="numeric"
          autoComplete="cc-csc"
          maxLength={4}
          value={cvv}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          onChange={(e) => setCvv(digitsOnly(e.target.value).slice(0, 4))}
        />
      </div>
      <button
        type="button"
        onClick={handlePay}
        disabled={procesando}
        className="btn-primary w-full"
      >
        <CreditCard className="h-4 w-4" />
        {procesando ? 'Procesando pago...' : 'Pagar con tarjeta'}
      </button>
      {cargaError && (
        <p className="text-center text-xs text-red-600">{cargaError}</p>
      )}
      {!formValid && (
        <p className="text-center text-xs text-amber-700">
          Complete contacto, dirección
          {payload.requiere_factura ? ', RFC y razón social' : ''} y acepte los términos para pagar.
        </p>
      )}
    </div>
  );
}
